import { CommonEventsEnum } from '@hubxtech/hubx-client-analytics-package/lib/src/enum/common/commonEventsEnum';
import Cookies from 'js-cookie';

import { hubxAnalytic } from '@/app/providers/analytics';
import { store } from '@/app/providers/store';
import { selectAuth } from '@/features/auth/store/auth';

import { getGoogleAnalyticsClientId } from '../hooks/client-id';
import { getCurrentUrl } from '../hooks/route-change';
import { eventTracker } from '../utils/event-tracker';
import { getUtmParams } from '../utils/get-utm-params';

type LogHubxEventParams = Parameters<typeof hubxAnalytic.sendEvent>[0];

function hasEventSource(value: unknown): value is { source: string } {
  return (
    value !== null &&
    typeof value === 'object' &&
    'source' in value &&
    typeof value.source === 'string'
  );
}

function getEventSource(params: LogHubxEventParams): string | undefined {
  const eventParams: unknown =
    'event_params' in params ? params.event_params : undefined;

  if (!hasEventSource(eventParams)) {
    return undefined;
  }

  return eventParams.source;
}

export function logHubxEvent(params: LogHubxEventParams): void {
  const loggingEnabled = import.meta.env.VITE_APP_ENV !== 'production';
  const currentUrl = getCurrentUrl();
  const eventName: string = params.eventName;
  const source = getEventSource(params);

  // Check for duplicate events on the same route
  const { isDuplicate, previousTimestamp, currentTrace } =
    eventTracker.trackEvent(eventName, currentUrl, source);

  if (isDuplicate) {
    if (loggingEnabled) {
      const errorMessage = `Duplicate event detected: "${eventName}" on route "${currentUrl}". Previous event timestamp: ${previousTimestamp}`;

      // eslint-disable-next-line no-console
      console.group('[Analytics] Duplicate Event Error');
      // eslint-disable-next-line no-console
      console.error(errorMessage);

      if (currentTrace !== undefined && currentTrace !== '') {
        // eslint-disable-next-line no-console
        console.log('🔍 Current duplicate event triggered from:');
        // eslint-disable-next-line no-console
        console.log(currentTrace);
      }

      // eslint-disable-next-line no-console
      console.groupEnd();
    }

    if (eventName.startsWith('lnd_') && loggingEnabled) {
      // eslint-disable-next-line no-console
      console.info('🔍 Duplicate event not triggered:', eventName);
    }
  }

  const clientId = getGoogleAnalyticsClientId();
  const auth = selectAuth(store.getState());
  const utmParams = getUtmParams();

  // Experiments & Versioning
  const experimentName = Cookies.get('gb_experiment');
  const experimentVariant = Cookies.get('gb_variation');

  const firstVisit = Cookies.get('first_visit_timestamp');
  const firstVisitTimestamp = firstVisit ?? new Date().toISOString();

  if (
    firstVisit === undefined &&
    eventName !== (CommonEventsEnum.CUSTOM_FIRST_VISIT as string)
  ) {
    Cookies.set('first_visit_timestamp', firstVisitTimestamp, {
      expires: 365,
      sameSite: 'Lax' as const,
      secure: import.meta.env.VITE_APP_ENV === 'production',
    });

    logHubxEvent({
      eventName: CommonEventsEnum.CUSTOM_FIRST_VISIT,
    });
  }

  void hubxAnalytic.sendEvent({
    ...params,
    user_id:
      typeof params.user_id === 'string' ? params.user_id : auth?.user?.uid,
    user_pseudo_id: clientId,
    event_timestamp: new Date().toISOString(),
    install_timestamp: firstVisitTimestamp,
    user_properties: {
      ...utmParams,
      experiment_name: experimentName,
      experiment_variant: experimentVariant,
      user_agent: navigator.userAgent,
    },
    url_params: {
      current_url: window.location.origin + currentUrl,
      previous_url:
        window.previous_url != null
          ? window.location.origin + window.previous_url
          : undefined,
    },
  });
}

export const COMMON_EVENTS = CommonEventsEnum;
